import React, { useState } from "react";
import "./ReconDetails.css";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getStatusIcon,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";
import AppLoader from "../../components/AppLoader/appLoader";
import { useReconDetails } from "../../customHooks/Recons/useReconDetails";
import UnAuthorized from "../UnAuthorized/UnAuthorized";
import usePermission from "../../utils/usePermission";
import { useSelector } from "react-redux";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import CopyIcon from "../../components/CopyIcon/CopyIcon";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

function ReconDetails() {
  const {
    transactionData,
    loading,
    error,
    handleOrderClick,
    activeOrderIndex,
    handleMessageTabClick,
    activeMessageTab,
    handleTabClick,
    activeTab,
    handleLatestSettlement,
    handleRaiseRecon,
    isSubmittingIssue,
    paymentId,
    setPaymentId,
    toSubscriberId,
    setToSubscriberId,
    toSubscriberUri,
    setToSubscriberUri,
    handleOpenIssueModal,
    handleCloseIssueModal,
    handleRaiseIssueSubmit,
    handleGoBack,
  } = useReconDetails();

  const { userData } = useSelector((state) => state.loginSlice);
  const [showIssueModal, setShowIssueModal] = useState(false);

  const canViewReconDetails = usePermission("RECON_DETAILS_VIEW");
  const canViewIGM = usePermission("IGM_VIEW");

  if (loading) return <AppLoader />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!canViewReconDetails) return <UnAuthorized />;
  if (!transactionData?.orders || !Array.isArray(transactionData.orders)) {
    return <div className="alert alert-warning">No order data available.</div>;
  }

  const orders = Array.isArray(transactionData?.orders)
    ? transactionData.orders
    : [];

  if (!orders.length) {
    return (
      <div className="alert alert-warning">No orders found for this recon.</div>
    );
  }

  const currentOrder =
    orders.length > activeOrderIndex ? orders[activeOrderIndex] : null;

  if (!currentOrder) {
    return (
      <div className="alert alert-warning">Selected order is not valid.</div>
    );
  }

  const {
    order_id,
    order_status,
    order_amount,
    settled_amount,
    remaining_balance,
    last_action,
    order_summary,
    taxes_charges_summary,
    messages: recon_messages,
  } = currentOrder;

  const renderSummaryRow = (label, value) => (
    <div className="summary-row d-flex justify-content-between py-1 border-bottom">
      <span className="summary-label">{label}</span>
      <span className="summary-value fw-semibold">{formatCurrency(value)}</span>
    </div>
  );

  return (
    <>
      {canViewReconDetails ? (
        <div className="recon-details-container d-flex flex-column mt-2">
          <button
            className="btn btn-link text-primary p-0 d-flex align-items-center text-decoration-none me-3"
            onClick={handleGoBack}
            style={{ fontSize: "16px", fontWeight: "500" }}
            aria-label="Go back to orders list"
          >
            <ArrowLeft size={20} style={{ marginRight: "5px" }} />
            Back
          </button>
          <div className="d-flex flex-fill overflow-hidden">
            <div className="recon-sidebar d-flex flex-column">
              <div className="p-2 border-bottom  bg-light">
                <h6 className="mb-0 fw-semibold text-muted text-uppercase small">
                  Order IDs
                </h6>
              </div>
              <div className="flex-fill overflow-auto">
                {orders.map((order, index) => (
                  <div
                    key={order?.order_id || index}
                    className={`list-item p-2 ${
                      activeOrderIndex === index ? "active" : ""
                    }`}
                    onClick={() => handleOrderClick(index)}
                  >
                    <div className="fw-medium text-dark small">
                      {order?.order_id || `Order #${index + 1}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-fill px-4 overflow-auto">
              <div className="transaction-detail-card  border-0 shadow-sm mb-4">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 fw-bold mb-4">Transaction Detail</h5>
                </div>
                <div style={{ fontSize: "14px", fontWeight: "500" , marginBottom: "10px" }}>
                  Order ID: {order_id}
                </div>

                <div className="row">
                  <div className="col">
                    <div className="detail-item">
                      <label className="detail-label">Order Status</label>
                      <div className="detail-value">
                        <span
                          className={`status-badge badge bg-${mapStatusToBootstrap(
                            order_status
                          )}`}
                        >
                          {order_status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="detail-item">
                      <label className="detail-label">Order Amount</label>
                      <div className="detail-value fw-bold text-primary">
                        {formatCurrency(order_amount)}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="detail-item">
                      <label className="detail-label">Settled Amount</label>
                      <div className="detail-value fw-bold text-success">
                        {formatCurrency(settled_amount)}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="detail-item">
                      <label className="detail-label">Remaining Balance</label>
                      <div className="detail-value fw-bold text-warning">
                        {formatCurrency(remaining_balance)}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="detail-item">
                      <label className="detail-label">Last Action Taken</label>
                      <div className="detail-value">
                        {last_action?.action} -{" "}
                        <small className="text-muted">
                          {formatDate(last_action?.timestamp)}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-summary-card card shadow-sm mb-4">
                <div className="card-header bg-light border-0 p-2">
                  <div className="d-flex align-items-center">
                    <div className="header-icon me-2">
                      <i className="fas fa-box"></i>
                    </div>
                    <h6 className="mb-0 fw-bold">ORDER SUMMARY – {order_id}</h6>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-sm table-bordered-dark align-middle mb-0"
                      style={{ width: "100%" }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th className="text-start px-3">Product Name</th>
                          <th>Qty</th>
                          <th>Unit Price (₹)</th>
                          <th>Total (₹)</th>
                          <th>GST (%)</th>
                          <th>GST Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order_summary?.products?.map((item, idx) => (
                          <tr key={idx}>
                            <td className="text-start px-3">
                              {item.item_name}
                            </td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.unit_price)}</td>
                            <td>
                              {formatCurrency(item.calc_product_total_price)}
                            </td>
                            <td>
                              <span className="gst-badge">
                                {item.product_gst_percentage}%
                              </span>
                            </td>
                            <td>
                              {formatCurrency(item.calc_product_gst_amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Delivery Charges Section Outside Table */}
                  <div
                    className="delivery-summary  p-2"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <h6 className="mb-0 fw-bold">Delivery Charges</h6>
                    <div className="d-flex justify-content-between">
                      <div>Amount:</div>
                      <div>
                        {formatCurrency(
                          order_summary?.delivery_charges?.amount
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between">
                      <div>GST:</div>
                      <div>
                        {order_summary?.delivery_charges?.gst_percent}% (
                        {formatCurrency(
                          order_summary?.delivery_charges?.gst_amount
                        )}
                        )
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-header bg-light border-0 mt-2">
                  <div className="d-flex align-items-center">
                    <div className="header-icon me-2">
                      <i className="fas fa-calculator"></i>
                    </div>
                    <h6 className="mb-0 fw-bold">Taxes & Charges Summary</h6>
                  </div>
                </div>
                <div className="card-body">
                  <div className="taxes-summary">
                    {renderSummaryRow(
                      "Buyer Finder Fee (Commission)",
                      taxes_charges_summary?.buyer_finder_fee
                    )}
                    {renderSummaryRow("TCS", taxes_charges_summary?.tcs)}
                    {renderSummaryRow("TDS", taxes_charges_summary?.tds)}
                    {renderSummaryRow(
                      "Withholding Amount",
                      taxes_charges_summary?.withholding_amount
                    )}
                    <div className="total-amount d-flex justify-content-between">
                      <span className="fw-bold fs-6">Total Order Amount</span>
                      <span className="fw-bold fs-6 text-primary">
                        {formatCurrency(order_summary.total_order_amount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold text-dark">Reconciliation</h6>
                  <div className="d-flex align-items-center gap-3"></div>
                </div>
                <div className="card-body p-0">
                  {recon_messages && recon_messages.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table mb-0 align-middle">
                        <thead className="bg-light">
                          <tr>
                            <th className="text-center ps-4 fw-bold">
                              Recon Message ID
                            </th>
                            <th className="text-center fw-bold">Raised By</th>
                            <th className="text-center fw-bold">Raised To</th>
                            <th className="text-center fw-bold">Status</th>
                            <th className="text-center fw-bold">Remarks</th>
                            <th className="text-center pe-4 fw-bold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recon_messages.map((msg, msgIndex) => {
                            const isExpanded = activeTab === msgIndex;
                            const reconStatus = (msg.recon_status || "")
                              .toString()
                              .toUpperCase()
                              .replace(/[_\s]+/g, " ")
                              .trim();

                            return (
                              <React.Fragment
                                key={msg.recon_sent?.message_id || msgIndex}
                              >
                                <tr
                                  className="cursor-pointer"
                                  onClick={() => handleTabClick(msgIndex)}
                                >
                                  <td className="ps-4 font-monospace small fw-bold">
                                    <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip
                                            id={`recon-msg-id-${msgIndex}`}
                                          >
                                            {msg.recon_sent?.message_id}
                                          </Tooltip>
                                        }
                                      >
                                        <span
                                          className="truncate-cell font-monospace fw-bold"
                                          style={{ maxWidth: "150px" }}
                                        >
                                          {msg.recon_sent?.message_id?.substring(
                                            0,
                                            12
                                          ) || "N/A"}
                                          ...
                                        </span>
                                      </OverlayTrigger>
                                      <CopyIcon
                                        textToCopy={msg.recon_sent?.message_id}
                                      />
                                    </div>
                                  </td>
                                  <td className="text-center small">
                                    {msg.recon_raise_by || "N/A"}
                                  </td>
                                  <td className="text-center small">
                                    {msg.recon_raised_to || "N/A"}
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-warning-subtle text-warning-emphasis">
                                      {msg.recon_status || "N/A"}
                                    </span>
                                  </td>
                                  <td className="text-center small">
                                    {Array.isArray(msg.remarks) &&
                                    msg.remarks.length > 0 ? (
                                      <div style={{ textAlign: "left" }}>
                                        {msg.remarks.map((remark, idx) => (
                                          <div
                                            key={idx}
                                            style={{
                                              marginBottom: "6px",
                                              padding: "4px 0",
                                            }}
                                          >
                                            • {remark}
                                          </div>
                                        ))}
                                      </div>
                                    ) : typeof msg.remarks === "string" ? (
                                      msg.remarks || "N/A"
                                    ) : (
                                      "N/A"
                                    )}
                                  </td>
                                  <td className="text-center pe-4">
                                    <button
                                      className="btn btn-sm p-1"
                                      style={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "50%",
                                        width: 32,
                                        height: 32,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        backgroundColor: "transparent",
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleTabClick(msgIndex);
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp
                                          size={16}
                                          className="text-secondary"
                                        />
                                      ) : (
                                        <ChevronDown
                                          size={16}
                                          className="text-secondary"
                                        />
                                      )}
                                    </button>
                                  </td>
                                </tr>

                                {isExpanded && (
                                  <tr>
                                    <td
                                      colSpan="6"
                                      style={{
                                        padding: "20px 16px",
                                        backgroundColor: "#fff",
                                      }}
                                    >
                                      <div style={{ display: "block", width: "100%" }}>
                                        <div
                                          style={{
                                            width: "100%",
                                            overflowX: "auto",
                                            borderCollapse: "collapse",
                                          }}
                                        >
                                          <table
                                            style={{
                                              width: "100%",
                                              fontSize: "12px",
                                              borderCollapse: "collapse",
                                            }}
                                          >
                                            <thead>
                                              <tr
                                                style={{
                                                  backgroundColor: "#f5f5f5",
                                                  borderBottom: "1px solid #d0d0d0",
                                                  width: "100%",
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Settlement ID
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Payment ID
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Status
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Amount (₹)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Commission (₹)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  TCS
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  TDS
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Withholding
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Settlement Ref No
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px 8px",
                                                    textAlign: "center",
                                                    fontSize: "10px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.3px",
                                                    color: "#666",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Date
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {msg.recon_sent?.settlements?.map(
                                                (settlement, idx) => (
                                                  <React.Fragment
                                                    key={`sent-${idx}`}
                                                  >
                                                    <tr
                                                      style={{
                                                        backgroundColor:
                                                          "#ffcdd2",
                                                        borderBottom:
                                                          "1px solid #e0e0e0",
                                                      }}
                                                    >
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          fontSize: "12px",
                                                          fontFamily: "monospace",
                                                        }}
                                                      >
                                                        <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                          <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                              <Tooltip
                                                                id={`settlement-id-sent-${idx}`}
                                                              >
                                                                {
                                                                  settlement.settlement_id
                                                                }
                                                              </Tooltip>
                                                            }
                                                          >
                                                            <span
                                                              className="truncate-cell font-monospace  fw-bold"
                                                              style={{
                                                                maxWidth: "150px",
                                                              }}
                                                            >
                                                              {settlement.settlement_id ||
                                                                "N/A"}
                                                            </span>
                                                          </OverlayTrigger>
                                                          <CopyIcon
                                                            textToCopy={
                                                              settlement.settlement_id
                                                            }
                                                          />
                                                        </div>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                          <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                              <Tooltip
                                                                id={`payment-id-sent-${idx}`}
                                                              >
                                                                {
                                                                  settlement.payment_id
                                                                }
                                                              </Tooltip>
                                                            }
                                                          >
                                                            <span
                                                              className="truncate-cell font-monospace  fw-bold"
                                                              style={{
                                                                maxWidth: "150px",
                                                              }}
                                                            >
                                                              {settlement.payment_id ||
                                                                "N/A"}
                                                            </span>
                                                          </OverlayTrigger>
                                                          <CopyIcon
                                                            textToCopy={
                                                              settlement.payment_id
                                                            }
                                                          />
                                                        </div>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            display: "inline-block",
                                                            backgroundColor: "#fff3cd",
                                                            color: "#856404",
                                                            padding: "3px 8px",
                                                            borderRadius: "3px",
                                                            fontSize: "11px",
                                                            fontWeight: 500,
                                                          }}
                                                        >
                                                          {(
                                                            settlement.status ||
                                                            ""
                                                          ).replace(/_/g, " ")}
                                                        </span>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        <div>
                                                          {formatCurrency(
                                                            settlement.amount || 0
                                                          )}
                                                        </div>
                                                        {reconStatus ===
                                                          "ON RECON RECEIVED" && (
                                                          <div
                                                            style={{
                                                              fontSize: "10px",
                                                              color: "#666",
                                                            }}
                                                          >
                                                            {"("}
                                                            {formatCurrency(
                                                              currentOrder?.seller_net_payout || 0
                                                            )}{" "}
                                                            +{" "}
                                                            {formatCurrency(
                                                              currentOrder?.msn_fee || 0
                                                            )}
                                                            {")"}
                                                          </div>
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          color: "#d32f2f",
                                                        }}
                                                      >
                                                        {settlement.commission > 0 && "-"}
                                                        {formatCurrency(
                                                          settlement.commission || 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.tcs ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.tds ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.withholding ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {settlement.ref_no || "NA"}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          color: "#999",
                                                        }}
                                                      >
                                                        {formatDateTime(settlement.date) ||
                                                          "N/A"}
                                                      </td>
                                                    </tr>

                                                    {msg.on_recon_received?.settlements?.find(
                                                      (s) =>
                                                        s.settlement_id ===
                                                        settlement.settlement_id
                                                    ) &&
                                                      (() => {
                                                        const receivedSettlement =
                                                          msg.on_recon_received.settlements.find(
                                                            (s) =>
                                                              s.settlement_id ===
                                                              settlement.settlement_id
                                                          );
                                                        return (
                                                          <tr
                                                            key={`received-${idx}`}
                                                            style={{
                                                              backgroundColor:
                                                                "#fffacd",
                                                              borderBottom:
                                                                "1px solid #e0e0e0",
                                                            }}
                                                          >
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                fontSize: "12px",
                                                                fontFamily: "monospace",
                                                              }}
                                                            >
                                                              <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={
                                                                    <Tooltip
                                                                      id={`settlement-id-received-${idx}`}
                                                                    >
                                                                      {
                                                                        receivedSettlement.settlement_id
                                                                      }
                                                                    </Tooltip>
                                                                  }
                                                                >
                                                                  <span
                                                                    className="truncate-cell font-monospace fw-bold"
                                                                    style={{
                                                                      maxWidth: "150px",
                                                                    }}
                                                                  >
                                                                    {receivedSettlement.settlement_id ||
                                                                      "N/A"}
                                                                  </span>
                                                                </OverlayTrigger>
                                                                <CopyIcon
                                                                  textToCopy={
                                                                    receivedSettlement.settlement_id
                                                                  }
                                                                />
                                                              </div>
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={
                                                                    <Tooltip
                                                                      id={`payment-id-received-${idx}`}
                                                                    >
                                                                      {
                                                                        receivedSettlement.payment_id
                                                                      }
                                                                    </Tooltip>
                                                                  }
                                                                >
                                                                  <span
                                                                    className="truncate-cell font-monospace fw-bold"
                                                                    style={{
                                                                      maxWidth: "150px",
                                                                    }}
                                                                  >
                                                                    {receivedSettlement.payment_id ||
                                                                      "N/A"}
                                                                  </span>
                                                                </OverlayTrigger>
                                                                <CopyIcon
                                                                  textToCopy={
                                                                    receivedSettlement.payment_id
                                                                  }
                                                                />
                                                              </div>
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  display: "inline-block",
                                                                  backgroundColor: "#fff3cd",
                                                                  color: "#856404",
                                                                  padding: "3px 8px",
                                                                  borderRadius: "3px",
                                                                  fontSize: "11px",
                                                                  fontWeight: 500,
                                                                }}
                                                              >
                                                                {(
                                                                  receivedSettlement.status ||
                                                                  ""
                                                                ).replace(/_/g, " ")}
                                                              </span>
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                receivedSettlement.amount || 0
                                                              )}
                                                              {reconStatus ===
                                                                "ON RECON RESPOND" && (
                                                                <div
                                                                  style={{
                                                                    fontSize: "10px",
                                                                    color: "#666",
                                                                  }}
                                                                >
                                                                  {"("}
                                                                  {formatCurrency(
                                                                    currentOrder?.seller_net_payout || 0
                                                                  )}{" "}
                                                                  +{" "}
                                                                  {formatCurrency(
                                                                    currentOrder?.msn_fee || 0
                                                                  )}
                                                                  {")"}
                                                                </div>
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                                color: "#d32f2f",
                                                              }}
                                                            >
                                                              {receivedSettlement.commission > 0 &&
                                                                "-"}
                                                              {formatCurrency(
                                                                receivedSettlement.commission || 0
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                receivedSettlement.tcs ?? 0
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                receivedSettlement.tds ?? 0
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              {formatCurrency(
                                                                receivedSettlement.withholding ?? 0
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              {receivedSettlement.ref_no || "N/A"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding: "10px 8px",
                                                                textAlign: "center",
                                                                fontSize: "12px",
                                                                color: "#999",
                                                              }}
                                                            >
                                                              {formatDateTime(
                                                                receivedSettlement.date
                                                              ) || "N/A"}
                                                            </td>
                                                          </tr>
                                                        );
                                                      })()}
                                                  </React.Fragment>
                                                )
                                              )}

                                              {msg.on_recon_received?.settlements?.map(
                                                (settlement, idx) => {
                                                  const hasMatch =
                                                    msg.recon_sent?.settlements?.some(
                                                      (s) =>
                                                        s.settlement_id ===
                                                        settlement.settlement_id
                                                    );
                                                  if (hasMatch) return null;

                                                  return (
                                                    <tr
                                                      key={`received-only-${idx}`}
                                                      style={{
                                                        backgroundColor:
                                                          "#fffacd",
                                                        borderBottom:
                                                          "1px solid #e0e0e0",
                                                      }}
                                                    >
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          fontSize: "12px",
                                                          fontFamily: "monospace",
                                                        }}
                                                      >
                                                        <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                          <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                              <Tooltip
                                                                id={`settlement-id-received-only-${idx}`}
                                                              >
                                                                {
                                                                  settlement.settlement_id
                                                                }
                                                              </Tooltip>
                                                            }
                                                          >
                                                            <span
                                                              className="truncate-cell font-monospace fw-bold"
                                                              style={{
                                                                maxWidth: "150px",
                                                              }}
                                                            >
                                                              {settlement.settlement_id ||
                                                                "N/A"}
                                                            </span>
                                                          </OverlayTrigger>
                                                          <CopyIcon
                                                            textToCopy={
                                                              settlement.settlement_id
                                                            }
                                                          />
                                                        </div>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-4">
                                                          <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                              <Tooltip
                                                                id={`payment-id-received-only-${idx}`}
                                                              >
                                                                {
                                                                  settlement.payment_id
                                                                }
                                                              </Tooltip>
                                                            }
                                                          >
                                                            <span
                                                              className="truncate-cell font-monospace fw-bold"
                                                              style={{
                                                                maxWidth: "150px",
                                                              }}
                                                            >
                                                              {settlement.payment_id ||
                                                                "N/A"}
                                                            </span>
                                                          </OverlayTrigger>
                                                          <CopyIcon
                                                            textToCopy={
                                                              settlement.payment_id
                                                            }
                                                          />
                                                        </div>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            display: "inline-block",
                                                            backgroundColor: "#fff3cd",
                                                            color: "#856404",
                                                            padding: "3px 8px",
                                                            borderRadius: "3px",
                                                            fontSize: "11px",
                                                            fontWeight: 500,
                                                          }}
                                                        >
                                                          {(
                                                            settlement.status ||
                                                            ""
                                                          ).replace(/_/g, " ")}
                                                        </span>
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.amount || 0
                                                        )}
                                                        {reconStatus ===
                                                          "ON RECON RESPOND" && (
                                                          <div
                                                            style={{
                                                              fontSize: "10px",
                                                              color: "#666",
                                                            }}
                                                          >
                                                            {"("}
                                                            {formatCurrency(
                                                              currentOrder?.seller_net_payout || 0
                                                            )}{" "}
                                                            +{" "}
                                                            {formatCurrency(
                                                              currentOrder?.msn_fee || 0
                                                            )}
                                                            {")"}
                                                          </div>
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          color: "#d32f2f",
                                                        }}
                                                      >
                                                        {settlement.commission > 0 && "-"}
                                                        {formatCurrency(
                                                          settlement.commission || 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.tcs ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.tds ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {formatCurrency(
                                                          settlement.withholding ?? 0
                                                        )}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {settlement.ref_no || "N/A"}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "10px 8px",
                                                          textAlign: "center",
                                                          fontSize: "12px",
                                                          color: "#999",
                                                        }}
                                                      >
                                                        {formatDateTime(settlement.date) ||
                                                          "N/A"}
                                                      </td>
                                                    </tr>
                                                  );
                                                }
                                              )}
                                            </tbody>
                                          </table>
                                        </div>

                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "12px",
                                            padding: "16px 0",
                                            backgroundColor: "#fff",
                                            borderTop: "1px solid #e0e0e0",
                                            marginTop: "16px",
                                          }}
                                        >
                                          {msg.on_recon_received?.status ===
                                          "On_Recon Rejected" ? (
                                            <>
                                              {!canViewIGM && (
                                                <button
                                                  className="btn btn-outline-danger"
                                                  onClick={() => setShowIssueModal(true)}
                                                >
                                                  Raise Issue
                                                </button>
                                              )}
                                              
                                            </>
                                          ) : (
                                            <button
                                              className="btn btn-primary"
                                              onClick={handleLatestSettlement}
                                            >
                                              Request Latest Settlement
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">
                      No reconciliation records found.
                    </div>
                  )}
                </div>
              </div>
            </div>
            {showIssueModal && (
              <div className="modal-backdrop">
                <div className="modal-dialog">
                  <form
                    className="modal-content"
                    onSubmit={handleRaiseIssueSubmit}
                  >
                    <div className="modal-header">
                      <h5 className="modal-title">Raise Issue</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          handleCloseIssueModal();
                          setShowIssueModal(false);
                        }}
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>
                        Please provide the following details to raise an issue.
                      </p>
                      <div className="mb-3">
                        <label htmlFor="paymentId" className="form-label">
                          Payment ID
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="paymentId"
                          value={paymentId}
                          onChange={(e) => setPaymentId(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="toSubscriberId" className="form-label">
                          To Subscriber ID
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="toSubscriberId"
                          value={toSubscriberId}
                          onChange={(e) => setToSubscriberId(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="toSubscriberUri" className="form-label">
                          To Subscriber URI
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="toSubscriberUri"
                          value={toSubscriberUri}
                          onChange={(e) => setToSubscriberUri(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary mx-2"
                        onClick={() => {
                          handleCloseIssueModal();
                          setShowIssueModal(false);
                        }}
                        disabled={isSubmittingIssue}
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmittingIssue}
                      >
                        {isSubmittingIssue ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            &nbsp;Submitting...
                          </>
                        ) : (
                          "Submit Issue"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <UnAuthorized />
      )}
    </>
  );
}

export default ReconDetails;
